/**
 * The module registry, as an administrator sees it (CLAUDE.md 12.2, 17).
 *
 * Two facts are shown side by side and must not be confused:
 *
 * * **verification** is a property of the *build*. It comes from the CI verification
 *   report and cannot be changed from here. Section 17 is absolute about that: a module
 *   that has not passed its suite is hidden from engineers, and no checkbox may
 *   override it — otherwise the one hard gate in the system becomes a preference.
 * * **enabled** is a property of *this instance*, and is what these controls change.
 *
 * Which is why a module can read "enabled" and still be unavailable to engineers: it is
 * switched on but nothing has passed verification. The "available to engineers" column
 * is the resolved answer, so nobody has to infer it.
 *
 * Disabling stops **new** positions. Existing positions pin their module version (5.7)
 * and keep recalculating, because a kill switch that stranded saved calculations could
 * never actually be used.
 */
import { useTranslation } from 'react-i18next'

import { useAdminModules, useUpdateAdminModule } from '@/api/admin'
import { ApiError } from '@/api/client'
import type { AdminModule } from '@/api/types'
import { Pill, TableScroller, Td, Th } from '@/components/admin/AdminShell'
import { VerificationBadge } from '@/components/calc/VerificationBadge'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'

export function AdminModules() {
  const { t } = useTranslation(['admin', 'common'])
  const { toast } = useToast()

  const modules = useAdminModules()
  const update = useUpdateAdminModule()

  if (modules.isLoading) return <Skeleton className="h-64" />

  const rows = modules.data ?? []
  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-border p-6 text-center text-sm text-text-muted">
        {t('admin.modules.empty')}
      </p>
    )
  }

  const patch = (moduleId: string, body: { enabled?: boolean; disabled_codes?: string[] }) =>
    update.mutate(
      { moduleId, ...body },
      {
        onSuccess: () => toast({ title: t('common.saved', { ns: 'common' }), variant: 'success' }),
        onError: (error) =>
          toast({
            title: t('common.error', { ns: 'common' }),
            description: error instanceof ApiError ? error.localised : undefined,
            variant: 'error',
          }),
      },
    )

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('admin.modules.title')}</h2>
      <p className="text-sm text-text-muted">{t('admin.modules.enabled_hint')}</p>

      <TableScroller>
        <thead>
          <tr>
            <Th>{t('common.module', { ns: 'common' })}</Th>
            <Th>{t('common.version', { ns: 'common' })}</Th>
            <Th>{t('admin.modules.verification')}</Th>
            <Th>{t('admin.modules.available_codes')}</Th>
            <Th className="text-end">{t('admin.modules.enabled')}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <ModuleRow key={row.module_id} row={row} onPatch={patch} />
          ))}
        </tbody>
      </TableScroller>
    </div>
  )
}

function ModuleRow({
  row,
  onPatch,
}: {
  row: AdminModule
  onPatch: (moduleId: string, body: { enabled?: boolean; disabled_codes?: string[] }) => void
}) {
  const { t } = useTranslation(['admin', 'common'])

  const toggleCode = (code: string) => {
    const next = row.disabled_codes.includes(code)
      ? row.disabled_codes.filter((c) => c !== code)
      : [...row.disabled_codes, code]
    onPatch(row.module_id, { disabled_codes: next })
  }

  return (
    <tr>
      <Td>
        {/* Short code and module id are identifiers, never translated (14.4). */}
        <div className="font-medium">{t(row.name_key, { defaultValue: row.module_id })}</div>
        <div className="font-mono text-xs text-text-muted">
          {row.short_code} · {row.module_id}
        </div>
        {row.experimental && <Pill tone="warn">experimental</Pill>}
        {row.envelope_key && (
          <div className="text-xs text-text-muted">
            {t('admin.modules.envelope')}: <span className="font-mono">{row.envelope_key}</span>
          </div>
        )}
      </Td>
      <Td className="font-mono text-xs">{row.version}</Td>
      <Td>
        <div className="flex flex-wrap gap-1">
          {row.supported_codes.map((code) => (
            <label key={code} className="flex items-center gap-1">
              <input
                type="checkbox"
                aria-label={code}
                // Checked means "usable"; the stored value is the inverse, because the
                // override records what is switched *off*.
                checked={!row.disabled_codes.includes(code)}
                disabled={!row.enabled}
                onChange={() => toggleCode(code)}
              />
              <VerificationBadge code={code} status={row.verification[code] ?? 'unverified'} />
            </label>
          ))}
        </div>
      </Td>
      <Td>
        {row.available_codes.length > 0 ? (
          <span className="font-mono text-xs">{row.available_codes.join(', ')}</span>
        ) : (
          <Pill tone="warn">{t('admin.modules.none_available')}</Pill>
        )}
      </Td>
      <Td>
        <div className="flex items-center justify-end gap-2">
          {!row.registry_enabled && (
            <Pill tone="muted">{t('admin.modules.disabled_by_registry')}</Pill>
          )}
          <input
            type="checkbox"
            aria-label={t('admin.modules.enabled')}
            checked={row.enabled}
            // The build's own flag wins. An override can only narrow (services/modules.py).
            disabled={!row.registry_enabled}
            onChange={(event) => onPatch(row.module_id, { enabled: event.target.checked })}
          />
        </div>
      </Td>
    </tr>
  )
}
